import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { DirectusService } from '../core/services/directus.service';

interface BirthdayMember {
  id: string;
  name: string;
}

interface UpcomingEvent {
  id: string;
  title: string;
  dateLabel: string;
}

interface StatCard {
  label: string;
  value: string;
  delta?: string;
  positive?: boolean;
  footnote: string;
}

interface EngagementMember {
  id: string;
  name: string;
  count: number;
  lastEventLabel: string;
}

interface EngagementBucket {
  key: 'dormant' | 'casual' | 'active' | 'power';
  label: string;
  range: string;
  count: number;
  members: EngagementMember[];
  tone: 'red' | 'amber' | 'blue' | 'green';
}

interface LeadershipBreakdown {
  key: string;
  label: string;
  count: number;
}

interface ReferrerEntry {
  id: string;
  name: string;
  count: number;
}

interface RecentEvent {
  id: string;
  title: string;
  dateLabel: string;
  registered: number;
  checkedIn: number;
  noShowPct: number;
}

interface CategoryMixRow {
  id: string;
  label: string;
  count: number;
  percent: number;
}

interface FunnelStage {
  label: string;
  count6: number;
  countYtd: number;
  convPct6: number | null;
  convPctYtd: number | null;
}

interface AcquisitionEvent {
  id: string;
  title: string;
  converted: number;
}

interface BoardMember {
  id: string;
  profileId: string;
  name: string;
  position: string;
  daysSinceLastEvent: number | null;
  attendedCount30: number;
  flagged: boolean;
  recentEventTitles: string[];
}

interface StaleDraftEvent {
  id: string;
  title: string;
  ageDays: number;
}

interface RenewalWatchMember {
  id: string;
  name: string;
  memberRank: number | null;
  subscribedUntilLabel: string;
}

@Component({
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dashboard">

      <!-- Loading -->
      <div *ngIf="loading" class="loading-state">
        <div class="loading-dots">
          <span></span><span></span><span></span>
        </div>
        <span>Loading dashboard…</span>
      </div>

      <ng-container *ngIf="!loading">

        <!-- ── Page header ──────────────────────────────────────────── -->
        <header class="page-header">
          <div class="page-header-text">
            <p class="page-eyebrow">Command view</p>
            <h1 class="page-title">Dashboard</h1>
            <p class="page-subtitle">Focused on growth, conversion, and event momentum.</p>
          </div>
          <div class="refresh-badge">
            <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
              <path d="M21 12a9 9 0 1 1-2.5-6.2L21 8V3" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ lastRefreshedLabel }}</span>
          </div>
        </header>

        <!-- ── Stat cards ───────────────────────────────────────────── -->
        <div class="stats-row">
          <div class="stat-card" *ngFor="let stat of statCards">
            <div class="stat-top">
              <span class="stat-label">{{ stat.label }}</span>
              <span
                *ngIf="stat.delta"
                class="stat-delta"
                [class.positive]="stat.positive"
                [class.negative]="!stat.positive"
              >
                {{ stat.delta }}
              </span>
            </div>
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-footer">{{ stat.footnote }}</div>
          </div>
        </div>

        <!-- ── Insight row ──────────────────────────────────────────── -->
        <div class="insight-row">

          <!-- Event momentum -->
          <div class="card">
            <div class="card-header">
              <h2 class="card-title">Event momentum</h2>
              <span class="card-badge">Pipeline</span>
            </div>
            <div class="metric-list">
              <div class="metric-row">
                <span class="metric-label">Next event</span>
                <strong class="metric-value">{{ nextEventLabel }}</strong>
              </div>
              <div class="metric-row">
                <span class="metric-label">Upcoming next 30 days</span>
                <strong class="metric-value">{{ upcomingNext30Count }}</strong>
              </div>
              <div class="metric-row">
                <span class="metric-label">Total upcoming pipeline</span>
                <strong class="metric-value">{{ upcomingEvents.length }}</strong>
              </div>
            </div>
          </div>

          <!-- Revenue pulse -->
          <div class="card">
            <div class="card-header">
              <h2 class="card-title">Revenue pulse</h2>
              <span class="card-badge card-badge--accent">{{ currentYearLabel }}</span>
            </div>
            <div class="kpi-grid">
              <div class="kpi-item">
                <strong class="kpi-value">{{ formatIdr(membershipRevenueYtd) }}</strong>
                <span class="kpi-label">Membership YTD</span>
              </div>
              <div class="kpi-item">
                <strong class="kpi-value">{{ formatIdr(ticketRevenueYtd) }}</strong>
                <span class="kpi-label">Ticket GMV YTD</span>
              </div>
              <div class="kpi-item">
                <strong class="kpi-value">{{ formatIdr(combinedRevenueThisMonth) }}</strong>
                <span class="kpi-label">Total this month</span>
              </div>
              <div class="kpi-item">
                <strong class="kpi-value">{{ formatIdr(avgRevenuePerMember) }}</strong>
                <span class="kpi-label">Avg / active member</span>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Engagement Health ─────────────────────────────────────── -->
        <div class="engagement-row">

          <div class="card engagement-card">
            <div class="card-header">
              <h2 class="card-title">Engagement distribution</h2>
              <span class="card-badge">Last 90 days</span>
            </div>
            <div class="engagement-body">
              <p class="engagement-explainer">
                {{ engagementEligibleCount }} members are eligible (joined ≥ 1 month ago).
                Click any bar for the member list.
              </p>
              <div class="bucket-grid">
                <button
                  type="button"
                  class="bucket"
                  *ngFor="let bucket of engagementBuckets"
                  [class.tone-red]="bucket.tone === 'red'"
                  [class.tone-amber]="bucket.tone === 'amber'"
                  [class.tone-blue]="bucket.tone === 'blue'"
                  [class.tone-green]="bucket.tone === 'green'"
                  [disabled]="bucket.count === 0"
                  (click)="openEngagementBucket(bucket)"
                >
                  <span class="bucket-count">{{ bucket.count }}</span>
                  <span class="bucket-label">{{ bucket.label }}</span>
                  <span class="bucket-range">{{ bucket.range }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="card engagement-card">
            <div class="card-header">
              <h2 class="card-title">Leadership participation</h2>
              <span class="card-badge card-badge--accent">{{ currentYearLabel }}</span>
            </div>
            <div class="engagement-body">
              <p class="engagement-explainer">
                <strong>{{ leadershipDistinctMembers }}</strong> members stepped up to serve in a project, committee, or contributor role this year.
              </p>
              <div class="leadership-list">
                <div class="leadership-row" *ngFor="let role of leadershipBreakdown">
                  <span class="leadership-label">{{ role.label }}</span>
                  <span class="leadership-count">{{ role.count }}</span>
                </div>
                <p *ngIf="leadershipDistinctMembers === 0" class="queue-empty">
                  No leadership roles recorded yet this year.
                </p>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Events split by job ───────────────────────────────────── -->
        <div class="engagement-row">

          <!-- Event performance -->
          <div class="card">
            <div class="card-header">
              <h2 class="card-title">Event performance</h2>
              <span class="card-badge">Internal value</span>
            </div>
            <div class="engagement-body">
              <p class="engagement-explainer">
                Avg unique-member reach (rolling 3 months):
                <strong>{{ avgMemberReachLabel }}</strong>
                across <strong>{{ pastEventsLast3Months }}</strong>
                event{{ pastEventsLast3Months === 1 ? '' : 's' }}.
              </p>

              <h4 class="subsection-title">Last 3 events</h4>
              <div class="metric-list metric-list--flush">
                <div class="metric-row metric-row--col" *ngFor="let e of recentEvents">
                  <div class="metric-row-head">
                    <span class="metric-label">{{ e.title }}</span>
                    <span class="metric-sub">{{ e.dateLabel }}</span>
                  </div>
                  <div class="metric-row-stats">
                    <span><strong>{{ e.checkedIn }}</strong> / {{ e.registered }} checked in</span>
                    <span class="metric-pill" [class.pill-warn]="e.noShowPct >= 30">
                      {{ e.noShowPct }}% no-show
                    </span>
                  </div>
                </div>
                <p *ngIf="recentEvents.length === 0" class="queue-empty">
                  No completed events yet.
                </p>
              </div>

              <h4 class="subsection-title">Category mix YTD</h4>
              <div class="category-mix">
                <div class="category-row" *ngFor="let c of categoryMix">
                  <div class="category-head">
                    <span class="category-label">{{ c.label }}</span>
                    <span class="category-count">{{ c.count }}</span>
                  </div>
                  <div class="category-bar">
                    <div class="category-bar-fill" [style.width.%]="c.percent"></div>
                  </div>
                </div>
                <p *ngIf="categoryMix.length === 0" class="queue-empty">
                  No categorised events this year yet.
                </p>
              </div>
            </div>
          </div>

          <!-- Acquisition funnel -->
          <div class="card">
            <div class="card-header">
              <h2 class="card-title">Acquisition funnel</h2>
              <span class="card-badge card-badge--accent">Events as marketing</span>
            </div>
            <div class="engagement-body">
              <p class="engagement-explainer">
                Non-member guests checking in to events, with how many returned and converted to paid members.
                Each stage shows <strong>6 mo</strong> · <strong>YTD</strong>.
              </p>

              <div class="funnel">
                <div class="funnel-stage" *ngFor="let s of acquisitionFunnel; let i = index">
                  <div class="funnel-stage-head">
                    <span class="funnel-stage-label">{{ s.label }}</span>
                    <span class="funnel-stage-conv" *ngIf="i > 0 && s.convPctYtd !== null">
                      {{ s.convPct6 }}% · {{ s.convPctYtd }}%
                    </span>
                  </div>
                  <div class="funnel-stage-counts">
                    <span class="funnel-num funnel-num--6mo">{{ s.count6 }}</span>
                    <span class="funnel-sep">·</span>
                    <span class="funnel-num funnel-num--ytd">{{ s.countYtd }}</span>
                  </div>
                  <div class="funnel-stage-sub">6 mo · YTD</div>
                </div>
              </div>

              <h4 class="subsection-title">Top acquisition events (this quarter)</h4>
              <div class="leadership-list">
                <div class="leadership-row" *ngFor="let e of topAcquisitionEvents">
                  <span class="leadership-label">{{ e.title }}</span>
                  <span class="leadership-count">{{ e.converted }}</span>
                </div>
                <p *ngIf="topAcquisitionEvents.length === 0" class="queue-empty">
                  No event-sourced conversions this quarter yet.
                </p>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Board & capacity ──────────────────────────────────────── -->
        <div class="engagement-row">

          <!-- Board health -->
          <div class="card">
            <div class="card-header">
              <h2 class="card-title">Board health</h2>
              <span
                class="card-badge"
                [class.card-badge--warn]="boardFlaggedCount > 0"
              >
                {{ boardFlaggedCount }} flagged
              </span>
            </div>
            <div class="engagement-body">
              <p class="engagement-explainer">
                LBOD members not seen at any event in the last 30 days are flagged.
                Click any board member for their recent activity.
              </p>
              <div class="board-list" *ngIf="boardMembers.length; else noBoard">
                <button
                  type="button"
                  class="board-row"
                  *ngFor="let m of boardMembers"
                  [class.board-row--flagged]="m.flagged"
                  (click)="openBoardMember(m)"
                >
                  <div class="board-row-main">
                    <div class="board-row-name">{{ m.name }}</div>
                    <div class="board-row-position">{{ m.position }}</div>
                  </div>
                  <div class="board-row-meta">
                    <span class="board-row-count">{{ m.attendedCount30 }} / 30d</span>
                    <span
                      class="board-row-pill"
                      [class.pill-danger]="m.flagged"
                      [class.pill-ok]="!m.flagged"
                    >
                      {{ boardLastSeenLabel(m) }}
                    </span>
                  </div>
                </button>
              </div>
              <ng-template #noBoard>
                <p class="queue-empty">
                  No LBOD roster found for {{ currentYearLabel }}.
                </p>
              </ng-template>
            </div>
          </div>

          <!-- Capacity tiles -->
          <div class="card">
            <div class="card-header">
              <h2 class="card-title">Operational capacity</h2>
              <span class="card-badge card-badge--accent">{{ currentYearLabel }}</span>
            </div>
            <div class="engagement-body">
              <div class="capacity-tiles">
                <div class="capacity-tile">
                  <span class="capacity-value">{{ activePicsThisMonth }}</span>
                  <span class="capacity-label">Active PICs this month</span>
                </div>
                <div class="capacity-tile">
                  <span class="capacity-value">{{ boardMembers.length }}</span>
                  <span class="capacity-label">LBOD members</span>
                </div>
                <div
                  class="capacity-tile"
                  [class.capacity-tile--warn]="staleDraftEvents.length > 0"
                >
                  <span class="capacity-value">{{ staleDraftEvents.length }}</span>
                  <span class="capacity-label">Stale draft events</span>
                </div>
              </div>

              <h4 class="subsection-title">Drafts idle &gt; 14 days</h4>
              <div class="leadership-list">
                <div class="leadership-row" *ngFor="let d of staleDraftEvents">
                  <span class="leadership-label">{{ d.title }}</span>
                  <span class="leadership-count">{{ d.ageDays }}d</span>
                </div>
                <p *ngIf="staleDraftEvents.length === 0" class="queue-empty">
                  No stale drafts. 🎉
                </p>
              </div>
            </div>
          </div>

        </div>

        <!-- ── Q4 Renewal watch (Oct–Dec only) ───────────────────────── -->
        <div class="card renewal-card" *ngIf="isRenewalSeason">
          <div class="card-header">
            <h2 class="card-title">Renewal watch</h2>
            <span class="card-badge card-badge--accent">{{ renewalRateLabel }}</span>
          </div>
          <div class="engagement-body">
            <p class="engagement-explainer">
              Members whose {{ currentYearLabel }} membership expires Dec 31 and have
              <strong>not yet renewed</strong> for next year. Bonus-plan carryovers are excluded.
            </p>
            <div class="leadership-list" *ngIf="renewalPending.length; else allRenewed">
              <div class="leadership-row" *ngFor="let m of renewalPending.slice(0, 5)">
                <span class="leadership-label">
                  {{ m.name }}
                  <span class="renewal-rank" *ngIf="m.memberRank !== null">· #{{ m.memberRank }}</span>
                </span>
                <span class="leadership-count renewal-pill">{{ m.subscribedUntilLabel }}</span>
              </div>
              <button
                *ngIf="renewalPending.length > 5"
                type="button"
                class="queue-more-btn"
                (click)="openRenewalAllModal()"
              >
                See all {{ renewalPending.length }}
              </button>
            </div>
            <ng-template #allRenewed>
              <p class="queue-empty">Everyone has renewed. 🎉</p>
            </ng-template>
          </div>
        </div>

        <!-- Renewal "see all" modal -->
        <div
          class="modal-backdrop"
          *ngIf="showRenewalAllModal"
          (click)="closeRenewalAllModal()"
        >
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
              <div>
                <h3 class="modal-title">Pending renewals</h3>
                <p class="modal-sub">{{ renewalPending.length }} members · {{ renewalRateLabel }}</p>
              </div>
              <button type="button" class="modal-close" (click)="closeRenewalAllModal()" aria-label="Close">×</button>
            </div>
            <div class="modal-body">
              <div class="modal-list">
                <div class="modal-row" *ngFor="let m of renewalPending">
                  <div class="modal-row-name">
                    {{ m.name }}
                    <span class="renewal-rank" *ngIf="m.memberRank !== null">· #{{ m.memberRank }}</span>
                  </div>
                  <div class="modal-row-meta">
                    <span>Expires {{ m.subscribedUntilLabel }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Board member modal ────────────────────────────────────── -->
        <div class="modal-backdrop" *ngIf="activeBoardMember" (click)="closeBoardMember()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
              <div>
                <h3 class="modal-title">{{ activeBoardMember.name }}</h3>
                <p class="modal-sub">{{ activeBoardMember.position }}</p>
              </div>
              <button type="button" class="modal-close" (click)="closeBoardMember()" aria-label="Close">×</button>
            </div>
            <div class="modal-body">
              <div class="board-modal-stats">
                <div>
                  <div class="board-stat-num">{{ activeBoardMember.attendedCount30 }}</div>
                  <div class="board-stat-label">events in last 30 days</div>
                </div>
                <div>
                  <div class="board-stat-num">
                    {{
                      activeBoardMember.daysSinceLastEvent === null
                        ? '—'
                        : activeBoardMember.daysSinceLastEvent + 'd'
                    }}
                  </div>
                  <div class="board-stat-label">since last event</div>
                </div>
              </div>
              <h4 class="subsection-title">Recent events attended</h4>
              <div class="modal-list" *ngIf="activeBoardMember.recentEventTitles.length; else noRecent">
                <div class="modal-row" *ngFor="let t of activeBoardMember.recentEventTitles">
                  <div class="modal-row-name">{{ t }}</div>
                </div>
              </div>
              <ng-template #noRecent>
                <p class="queue-empty">No event attendance recorded in the last 90 days.</p>
              </ng-template>
            </div>
          </div>
        </div>

        <!-- ── Priority queue ───────────────────────────────────────── -->
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Priority queue</h2>
            <span class="card-badge">Next actions</span>
          </div>
          <div class="queue-columns">

            <div class="queue-col">
              <p class="queue-col-title">
                <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
                  <path d="M7 2v4M17 2v4M3 9h18M4 6h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                Upcoming events
              </p>
              <div class="queue-list" *ngIf="upcomingEvents.length; else noUpcoming">
                <div class="queue-item" *ngFor="let event of upcomingEvents">
                  <span class="queue-dot"></span>
                  <div>
                    <div class="queue-item-title">{{ event.title }}</div>
                    <div class="queue-item-sub">{{ event.dateLabel }}</div>
                  </div>
                </div>
              </div>
              <ng-template #noUpcoming>
                <p class="queue-empty">No upcoming events scheduled.</p>
              </ng-template>
            </div>

            <div class="queue-col">
              <p class="queue-col-title">
                <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                Birthdays today
              </p>
              <div class="queue-list" *ngIf="birthdayMembers.length; else noBirthdays">
                <div class="queue-item" *ngFor="let member of birthdayMembers">
                  <span class="queue-dot queue-dot--yellow"></span>
                  <div>
                    <div class="queue-item-title">{{ member.name }}</div>
                    <div class="queue-item-sub">Send a greeting 🎂</div>
                  </div>
                </div>
              </div>
              <ng-template #noBirthdays>
                <p class="queue-empty">No birthdays today.</p>
              </ng-template>
            </div>

            <div class="queue-col">
              <p class="queue-col-title">
                <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
                  <path d="M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                Dormant members
              </p>
              <div class="queue-list" *ngIf="dormantPreview.length; else noDormant">
                <div class="queue-item" *ngFor="let m of dormantPreview">
                  <span class="queue-dot queue-dot--red"></span>
                  <div>
                    <div class="queue-item-title">{{ m.name }}</div>
                    <div class="queue-item-sub">{{ m.lastEventLabel }}</div>
                  </div>
                </div>
                <button
                  *ngIf="dormantBucket && dormantBucket.count > dormantPreview.length"
                  type="button"
                  class="queue-more-btn"
                  (click)="openEngagementBucket(dormantBucket)"
                >
                  See all {{ dormantBucket.count }}
                </button>
              </div>
              <ng-template #noDormant>
                <p class="queue-empty">No dormant members 🎉</p>
              </ng-template>
            </div>

            <div class="queue-col">
              <p class="queue-col-title">
                <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
                  <path d="M12 2 15 8l7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Top referrers (30d)
              </p>
              <div class="queue-list" *ngIf="topReferrers.length; else noReferrers">
                <div class="queue-item" *ngFor="let r of topReferrers">
                  <span class="queue-dot queue-dot--green"></span>
                  <div>
                    <div class="queue-item-title">{{ r.name }}</div>
                    <div class="queue-item-sub">{{ r.count }} referral{{ r.count === 1 ? '' : 's' }}</div>
                  </div>
                </div>
              </div>
              <ng-template #noReferrers>
                <p class="queue-empty">No referrals tracked in the last 30 days.</p>
              </ng-template>
            </div>

          </div>
        </div>

        <!-- ── Engagement bucket modal ───────────────────────────────── -->
        <div class="modal-backdrop" *ngIf="activeBucket" (click)="closeBucket()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
              <div>
                <h3 class="modal-title">{{ activeBucket.label }}</h3>
                <p class="modal-sub">
                  {{ activeBucket.range }} · {{ activeBucket.count }} member{{ activeBucket.count === 1 ? '' : 's' }}
                </p>
              </div>
              <button type="button" class="modal-close" (click)="closeBucket()" aria-label="Close">×</button>
            </div>
            <div class="modal-body">
              <div class="modal-list" *ngIf="activeBucket.members.length; else emptyBucket">
                <div class="modal-row" *ngFor="let m of activeBucket.members">
                  <div class="modal-row-name">{{ m.name }}</div>
                  <div class="modal-row-meta">
                    <span>{{ m.count }} event{{ m.count === 1 ? '' : 's' }}</span>
                    <span class="modal-row-sep">·</span>
                    <span>{{ m.lastEventLabel }}</span>
                  </div>
                </div>
              </div>
              <ng-template #emptyBucket>
                <p class="queue-empty">No members in this bucket.</p>
              </ng-template>
            </div>
          </div>
        </div>

      </ng-container>
    </div>
  `,
  styles: [
    `
      /* ── Dashboard shell ───────────────────────────────────────────── */
      .dashboard {
        display: flex;
        flex-direction: column;
        gap: 20px;
        width: 100%;
      }

      /* Loading */
      .loading-state {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 64px 0;
        justify-content: center;
        color: var(--app-muted);
        font-size: 14px;
      }
      .loading-dots {
        display: flex;
        gap: 5px;
      }
      .loading-dots span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--jci-blue);
        opacity: 0.4;
        animation: pulse 1.2s ease-in-out infinite;
      }
      .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
      .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes pulse {
        0%, 100% { opacity: 0.25; transform: scale(0.85); }
        50% { opacity: 1; transform: scale(1); }
      }

      /* ── Page header ───────────────────────────────────────────────── */
      .page-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        padding-bottom: 6px;
        border-bottom: 1px solid var(--app-border);
      }
      .page-eyebrow {
        margin: 0 0 6px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--jci-blue);
      }
      .page-title {
        margin: 0 0 5px;
        font-family: 'Arvo', serif;
        font-size: 2rem;
        font-weight: 700;
        color: var(--app-ink-strong);
        letter-spacing: -0.02em;
        line-height: 1;
      }
      .page-subtitle {
        margin: 0;
        font-size: 13.5px;
        color: var(--app-muted);
        line-height: 1.4;
      }
      .refresh-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        background: var(--app-surface-soft);
        border: 1px solid var(--app-border);
        border-radius: 999px;
        padding: 6px 13px;
        font-size: 12px;
        color: var(--app-muted);
        white-space: nowrap;
        flex-shrink: 0;
      }

      /* ── Stat cards ────────────────────────────────────────────────── */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
      }
      .stat-card {
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius);
        padding: 20px;
        box-shadow: var(--app-shadow);
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .stat-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
      }
      .stat-label {
        font-size: 11.5px;
        font-weight: 600;
        color: var(--app-muted);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        line-height: 1.3;
      }
      .stat-delta {
        font-size: 11px;
        font-weight: 700;
        border-radius: 4px;
        padding: 2px 7px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      .stat-delta.positive { color: #15803d; background: #dcfce7; }
      .stat-delta.negative { color: var(--app-danger); background: #fee2e2; }
      .stat-value {
        font-family: 'Arvo', serif;
        font-size: 2.2rem;
        font-weight: 700;
        color: var(--app-ink-strong);
        line-height: 1;
      }
      .stat-footer {
        font-size: 12px;
        color: var(--app-muted);
        padding-top: 10px;
        border-top: 1px solid var(--app-border);
        margin-top: auto;
        line-height: 1.3;
      }

      /* ── Insight row ───────────────────────────────────────────────── */
      .insight-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }

      /* ── Shared card ───────────────────────────────────────────────── */
      .card {
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius);
        box-shadow: var(--app-shadow);
        overflow: hidden;
      }
      .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px 20px;
        border-bottom: 1px solid var(--app-border);
      }
      .card-title {
        margin: 0;
        font-family: 'Arvo', serif;
        font-size: 14.5px;
        font-weight: 700;
        color: var(--app-ink-strong);
      }
      .card-badge {
        font-size: 11px;
        font-weight: 600;
        background: var(--app-surface-soft);
        border: 1px solid var(--app-border);
        border-radius: 999px;
        padding: 3px 10px;
        color: var(--app-muted);
        white-space: nowrap;
      }
      .card-badge--accent {
        background: rgba(239, 196, 15, 0.1);
        border-color: rgba(239, 196, 15, 0.35);
        color: #92680a;
      }

      /* Metric list */
      .metric-list {
        padding: 14px 16px 16px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .metric-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 10px 13px;
        border-radius: 7px;
        background: var(--app-surface-soft);
        border: 1px solid var(--app-border);
      }
      .metric-label {
        font-size: 13px;
        color: var(--app-muted);
      }
      .metric-value {
        font-size: 14px;
        font-weight: 700;
        color: var(--app-ink-strong);
      }

      /* KPI grid */
      .kpi-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
        padding: 14px 16px 16px;
      }
      .kpi-item {
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 13px 14px;
        border-radius: 7px;
        background: var(--app-surface-soft);
        border: 1px solid var(--app-border);
      }
      .kpi-value {
        font-size: 1.15rem;
        font-weight: 700;
        color: var(--jci-blue);
        line-height: 1;
      }
      .kpi-item--muted .kpi-value {
        color: var(--app-muted);
        font-size: 1rem;
      }
      .kpi-label {
        font-size: 11.5px;
        color: var(--app-muted);
        line-height: 1.3;
      }

      /* ── Priority queue ────────────────────────────────────────────── */
      .queue-columns {
        display: grid;
        grid-template-columns: 1fr 1fr;
      }
      .queue-col {
        padding: 16px 20px 20px;
      }
      .queue-col:first-child {
        border-right: 1px solid var(--app-border);
      }
      .queue-col-title {
        margin: 0 0 12px;
        font-size: 13px;
        font-weight: 700;
        color: var(--app-ink-strong);
        display: flex;
        align-items: center;
        gap: 7px;
      }
      .queue-col-title svg { color: var(--app-muted); flex-shrink: 0; }
      .queue-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .queue-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 13px;
        border-radius: 7px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
      }
      .queue-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--jci-blue);
        margin-top: 5px;
        flex-shrink: 0;
      }
      .queue-dot--yellow { background: var(--jci-yellow); }
      .queue-item-title {
        font-size: 13px;
        font-weight: 600;
        color: var(--app-ink-strong);
        line-height: 1.3;
      }
      .queue-item-sub {
        margin-top: 2px;
        font-size: 12px;
        color: var(--app-muted);
      }
      .queue-empty {
        margin: 0;
        padding: 12px 0;
        font-size: 13px;
        color: var(--app-muted);
      }

      /* ── Phase 3 — Events split by job ─────────────────────────────── */
      .subsection-title {
        margin: 18px 0 8px;
        font-size: 11px;
        font-weight: 700;
        color: var(--app-muted);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .metric-list--flush { padding: 0; }
      .metric-row--col {
        flex-direction: column;
        align-items: stretch;
        gap: 6px;
      }
      .metric-row-head {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .metric-sub {
        font-size: 11.5px;
        color: var(--app-muted);
      }
      .metric-row-stats {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        font-size: 12.5px;
        color: var(--app-text);
      }
      .metric-row-stats strong {
        color: var(--app-ink-strong);
      }
      .metric-pill {
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        background: #dcfce7;
        color: #15803d;
      }
      .metric-pill.pill-warn {
        background: #fee2e2;
        color: #b91c1c;
      }

      /* Category mix bars */
      .category-mix {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .category-row {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .category-head {
        display: flex;
        justify-content: space-between;
        font-size: 12.5px;
      }
      .category-label { color: var(--app-text); }
      .category-count {
        font-weight: 700;
        color: var(--app-ink-strong);
      }
      .category-bar {
        width: 100%;
        height: 6px;
        background: var(--app-surface-soft);
        border-radius: 999px;
        overflow: hidden;
      }
      .category-bar-fill {
        height: 100%;
        background: var(--jci-blue);
        border-radius: 999px;
      }

      /* Funnel */
      .funnel {
        display: flex;
        align-items: stretch;
        gap: 8px;
        margin-bottom: 6px;
      }
      .funnel-stage {
        flex: 1;
        padding: 14px 12px 12px;
        border-radius: 9px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
        display: flex;
        flex-direction: column;
        gap: 4px;
        position: relative;
      }
      .funnel-stage + .funnel-stage::before {
        content: '→';
        position: absolute;
        left: -10px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--app-muted);
        font-size: 14px;
      }
      .funnel-stage-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 6px;
      }
      .funnel-stage-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--app-text);
      }
      .funnel-stage-conv {
        font-size: 10.5px;
        font-weight: 700;
        color: var(--jci-blue);
        white-space: nowrap;
      }
      .funnel-stage-counts {
        display: flex;
        align-items: baseline;
        gap: 6px;
      }
      .funnel-num {
        font-family: 'Arvo', serif;
        font-weight: 700;
        line-height: 1;
      }
      .funnel-num--6mo {
        font-size: 1.5rem;
        color: var(--app-ink-strong);
      }
      .funnel-num--ytd {
        font-size: 1.05rem;
        color: var(--app-muted);
      }
      .funnel-sep { color: var(--app-muted); }
      .funnel-stage-sub {
        font-size: 10.5px;
        color: var(--app-muted);
      }

      /* ── Phase 5 — Renewal watch ───────────────────────────────────── */
      .renewal-card .leadership-label { display: flex; align-items: center; gap: 6px; }
      .renewal-rank {
        font-size: 11px;
        color: var(--app-muted);
        font-weight: 500;
      }
      .renewal-pill {
        font-size: 11.5px;
        padding: 3px 9px;
        border-radius: 999px;
        background: rgba(239, 196, 15, 0.15);
        color: #92680a;
        border: 1px solid rgba(239, 196, 15, 0.35);
      }

      /* ── Phase 4 — Board & capacity ────────────────────────────────── */
      .card-badge--warn {
        background: #fee2e2;
        border-color: #fecaca;
        color: #b91c1c;
      }
      .board-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .board-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 8px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
        cursor: pointer;
        text-align: left;
        font-family: inherit;
        transition: border-color 120ms, transform 120ms;
      }
      .board-row:hover {
        border-color: var(--jci-blue);
        transform: translateY(-1px);
      }
      .board-row--flagged {
        border-left: 3px solid #dc2626;
      }
      .board-row-main { min-width: 0; }
      .board-row-name {
        font-size: 13.5px;
        font-weight: 600;
        color: var(--app-ink-strong);
      }
      .board-row-position {
        font-size: 11.5px;
        color: var(--app-muted);
        margin-top: 2px;
      }
      .board-row-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }
      .board-row-count {
        font-size: 11.5px;
        color: var(--app-muted);
      }
      .board-row-pill {
        padding: 3px 9px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
      }
      .pill-ok {
        background: #dcfce7;
        color: #15803d;
      }
      .pill-danger {
        background: #fee2e2;
        color: #b91c1c;
      }

      /* Capacity tiles */
      .capacity-tiles {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        margin-bottom: 4px;
      }
      .capacity-tile {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        padding: 14px 14px 12px;
        border-radius: 9px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
      }
      .capacity-tile--warn {
        border-top: 3px solid #f59e0b;
      }
      .capacity-value {
        font-family: 'Arvo', serif;
        font-size: 1.6rem;
        font-weight: 700;
        color: var(--app-ink-strong);
        line-height: 1;
      }
      .capacity-label {
        font-size: 11.5px;
        color: var(--app-muted);
        line-height: 1.3;
      }

      /* Board modal stats */
      .board-modal-stats {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        margin-bottom: 4px;
      }
      .board-modal-stats > div {
        padding: 14px 14px 12px;
        border-radius: 9px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
      }
      .board-stat-num {
        font-family: 'Arvo', serif;
        font-size: 1.6rem;
        font-weight: 700;
        color: var(--jci-blue);
        line-height: 1;
      }
      .board-stat-label {
        margin-top: 4px;
        font-size: 11.5px;
        color: var(--app-muted);
      }

      /* ── Engagement Health row ─────────────────────────────────────── */
      .engagement-row {
        display: grid;
        grid-template-columns: 1.4fr 1fr;
        gap: 16px;
      }
      .engagement-card .engagement-body {
        padding: 16px 20px 20px;
      }
      .engagement-explainer {
        margin: 0 0 14px;
        font-size: 12.5px;
        color: var(--app-muted);
        line-height: 1.4;
      }
      .bucket-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
      }
      .bucket {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        padding: 14px 14px 12px;
        border-radius: 9px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
        cursor: pointer;
        text-align: left;
        font-family: inherit;
        transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
      }
      .bucket:hover:not(:disabled) {
        transform: translateY(-1px);
        border-color: var(--jci-blue);
        box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
      }
      .bucket:disabled {
        opacity: 0.55;
        cursor: default;
      }
      .bucket-count {
        font-family: 'Arvo', serif;
        font-size: 1.7rem;
        font-weight: 700;
        color: var(--app-ink-strong);
        line-height: 1;
      }
      .bucket-label {
        margin-top: 6px;
        font-size: 12px;
        font-weight: 700;
        color: var(--app-text);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .bucket-range {
        font-size: 11px;
        color: var(--app-muted);
      }
      .bucket.tone-red    { border-top: 3px solid #dc2626; }
      .bucket.tone-amber  { border-top: 3px solid #f59e0b; }
      .bucket.tone-blue   { border-top: 3px solid var(--jci-blue); }
      .bucket.tone-green  { border-top: 3px solid #16a34a; }

      /* Leadership list */
      .leadership-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .leadership-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 13px;
        border-radius: 7px;
        background: var(--app-surface-soft);
        border: 1px solid var(--app-border);
      }
      .leadership-label {
        font-size: 13px;
        color: var(--app-text);
      }
      .leadership-count {
        font-family: 'Arvo', serif;
        font-size: 1.05rem;
        font-weight: 700;
        color: var(--jci-blue);
      }

      /* Queue dot extras */
      .queue-dot--red { background: #dc2626; }
      .queue-dot--green { background: #16a34a; }

      /* See-all button in queue */
      .queue-more-btn {
        margin-top: 6px;
        padding: 8px 12px;
        border: 1px dashed var(--app-border);
        background: transparent;
        border-radius: 7px;
        font-size: 12px;
        font-weight: 600;
        color: var(--jci-blue);
        cursor: pointer;
        font-family: inherit;
      }
      .queue-more-btn:hover {
        background: var(--app-surface-soft);
        border-style: solid;
      }

      /* Queue 4-column responsive */
      .queue-columns {
        grid-template-columns: repeat(4, 1fr);
      }
      .queue-col {
        border-right: 1px solid var(--app-border);
      }
      .queue-col:last-child {
        border-right: none;
      }

      /* Modal */
      .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.5);
        backdrop-filter: blur(3px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
        padding: 24px;
      }
      .modal {
        background: var(--app-surface);
        border-radius: 14px;
        max-width: 560px;
        width: 100%;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        box-shadow: 0 20px 60px rgba(15, 23, 42, 0.3);
        overflow: hidden;
      }
      .modal-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 20px 22px 14px;
        border-bottom: 1px solid var(--app-border);
        gap: 12px;
      }
      .modal-title {
        margin: 0 0 4px;
        font-family: 'Arvo', serif;
        font-size: 17px;
        font-weight: 700;
        color: var(--app-ink-strong);
      }
      .modal-sub {
        margin: 0;
        font-size: 12.5px;
        color: var(--app-muted);
      }
      .modal-close {
        background: transparent;
        border: none;
        font-size: 26px;
        line-height: 1;
        color: var(--app-muted);
        cursor: pointer;
        padding: 0 4px;
      }
      .modal-close:hover { color: var(--app-text); }
      .modal-body {
        padding: 14px 22px 22px;
        overflow-y: auto;
      }
      .modal-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .modal-row {
        padding: 11px 13px;
        border-radius: 8px;
        border: 1px solid var(--app-border);
        background: var(--app-surface-soft);
      }
      .modal-row-name {
        font-size: 13.5px;
        font-weight: 600;
        color: var(--app-ink-strong);
        margin-bottom: 3px;
      }
      .modal-row-meta {
        font-size: 12px;
        color: var(--app-muted);
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .modal-row-sep { opacity: 0.5; }

      /* ── Responsive ────────────────────────────────────────────────── */
      @media (max-width: 1200px) {
        .stats-row { grid-template-columns: repeat(2, 1fr); }
        .queue-columns { grid-template-columns: repeat(2, 1fr); }
        .queue-col:nth-child(2) { border-right: none; }
        .queue-col:nth-child(1), .queue-col:nth-child(2) {
          border-bottom: 1px solid var(--app-border);
        }
      }
      @media (max-width: 900px) {
        .insight-row { grid-template-columns: 1fr; }
        .engagement-row { grid-template-columns: 1fr; }
        .bucket-grid { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 600px) {
        .stats-row { grid-template-columns: 1fr; }
        .queue-columns { grid-template-columns: 1fr; }
        .queue-col { border-right: none; border-bottom: 1px solid var(--app-border); }
        .queue-col:last-child { border-bottom: none; }
        .page-header { flex-direction: column; align-items: flex-start; }
        .kpi-grid { grid-template-columns: 1fr; }
        .bucket-grid { grid-template-columns: 1fr; }
      }
    `
  ]
})
export class DashboardComponent implements OnInit {
  upcomingEvents: UpcomingEvent[] = [];
  birthdayMembers: BirthdayMember[] = [];
  loading = true;
  lastRefreshed = new Date();

  // Phase 1 metrics
  activeMembers = 0;
  engagementRate = 0;          // 0-100
  engagedMemberCount = 0;
  guestConversionRate = 0;     // 0-100
  guestConversionCount = 0;
  newMembersYtd = 0;
  membershipRevenueYtd = 0;
  membershipRevenueLastYearSamePeriod = 0;
  ticketRevenueYtd = 0;
  ticketRevenueLastYearSamePeriod = 0;
  combinedRevenueThisMonth = 0;
  upcomingNext30Count = 0;

  // Phase 2 — engagement panel
  engagementBuckets: EngagementBucket[] = [
    { key: 'dormant', label: 'Dormant', range: '0 events', count: 0, members: [], tone: 'red' },
    { key: 'casual',  label: 'Casual',  range: '1–2 events', count: 0, members: [], tone: 'amber' },
    { key: 'active',  label: 'Active',  range: '3–5 events', count: 0, members: [], tone: 'blue' },
    { key: 'power',   label: 'Power',   range: '6+ events',  count: 0, members: [], tone: 'green' }
  ];
  engagementEligibleCount = 0;
  leadershipBreakdown: LeadershipBreakdown[] = [];
  leadershipDistinctMembers = 0;
  dormantPreview: EngagementMember[] = [];
  topReferrers: ReferrerEntry[] = [];
  activeBucket: EngagementBucket | null = null;

  // Phase 3 — events split by job
  recentEvents: RecentEvent[] = [];
  categoryMix: CategoryMixRow[] = [];
  pastEventsLast3Months = 0;
  uniqueMembersLast3Months = 0;
  acquisitionFunnel: FunnelStage[] = [
    { label: 'Guest check-ins', count6: 0, countYtd: 0, convPct6: null, convPctYtd: null },
    { label: 'Repeat guests', count6: 0, countYtd: 0, convPct6: 0, convPctYtd: 0 },
    { label: 'Converted', count6: 0, countYtd: 0, convPct6: 0, convPctYtd: 0 }
  ];
  topAcquisitionEvents: AcquisitionEvent[] = [];

  // Phase 4 — Board & capacity
  boardMembers: BoardMember[] = [];
  boardFlaggedCount = 0;
  activePicsThisMonth = 0;
  staleDraftEvents: StaleDraftEvent[] = [];
  activeBoardMember: BoardMember | null = null;

  // Phase 5 — Q4 renewal watch
  renewalEligibleCount = 0;
  renewalRenewedCount = 0;
  renewalPending: RenewalWatchMember[] = [];
  showRenewalAllModal = false;

  constructor(private readonly directus: DirectusService) {}

  get currentYearLabel(): string {
    return String(new Date().getFullYear());
  }

  get nextEventLabel(): string {
    return this.upcomingEvents[0]?.dateLabel ?? 'No upcoming event';
  }

  get combinedRevenueYtd(): number {
    return this.membershipRevenueYtd + this.ticketRevenueYtd;
  }

  get combinedRevenueLastYearSamePeriod(): number {
    return this.membershipRevenueLastYearSamePeriod + this.ticketRevenueLastYearSamePeriod;
  }

  get avgRevenuePerMember(): number {
    if (this.activeMembers <= 0) return 0;
    return this.combinedRevenueYtd / this.activeMembers;
  }

  get lastRefreshedLabel(): string {
    return this.lastRefreshed.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatIdr(value: number): string {
    if (!value || value <= 0) return 'Rp 0';
    return `Rp ${Math.round(value).toLocaleString('id-ID')}`;
  }

  private formatPctDelta(current: number, previous: number): { delta: string; positive: boolean } {
    if (previous <= 0 && current <= 0) return { delta: '—', positive: true };
    if (previous <= 0) return { delta: 'New', positive: true };
    const pct = ((current - previous) / previous) * 100;
    const rounded = Math.abs(pct) >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
    const sign = pct >= 0 ? '+' : '';
    return { delta: `${sign}${rounded}% YoY`, positive: pct >= 0 };
  }

  get statCards(): StatCard[] {
    const revenueDelta = this.formatPctDelta(
      this.combinedRevenueYtd,
      this.combinedRevenueLastYearSamePeriod
    );

    return [
      {
        label: 'Active members',
        value: String(this.activeMembers),
        footnote: `Subscribed for ${this.currentYearLabel} or beyond`
      },
      {
        label: 'Engagement rate (90d)',
        value: `${this.engagementRate.toFixed(0)}%`,
        footnote: `${this.engagedMemberCount} of ${this.activeMembers} attended ≥1 event`
      },
      {
        label: 'Event → member YTD',
        value: `${this.guestConversionRate.toFixed(0)}%`,
        footnote: `${this.guestConversionCount} of ${this.newMembersYtd} new members checked in to an event before joining`
      },
      {
        label: 'Revenue YTD',
        value: this.formatIdr(this.combinedRevenueYtd),
        delta: revenueDelta.delta,
        positive: revenueDelta.positive,
        footnote: `Membership ${this.formatIdr(this.membershipRevenueYtd)} · Tickets ${this.formatIdr(this.ticketRevenueYtd)}`
      }
    ];
  }

  async ngOnInit(): Promise<void> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yearStart = new Date(today.getFullYear(), 0, 1);
    const yearEnd = new Date(today.getFullYear(), 11, 31);
    const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
    const lastYearSameDay = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
    const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const todayIso = today.toISOString().slice(0, 10);
    const iso = (d: Date) => d.toISOString();
    const isoDate = (d: Date) => d.toISOString().slice(0, 10);

    const [
      activeMembers,
      upcoming,
      birthdays,
      engagement,
      guestConversion,
      membershipYtd,
      membershipLastYearSame,
      membershipThisMonth,
      ticketYtd,
      ticketLastYearSame,
      ticketThisMonth,
      upcoming30
    ] = await Promise.all([
      this.countCollection('profiles', { 'filter[subscribed_until][_gte]': todayIso }),
      this.loadUpcomingEvents(today),
      this.loadBirthdaysToday(today),
      this.loadEngagement(iso(ninetyDaysAgo)),
      this.loadGuestConversion(isoDate(yearStart)),
      this.sumSubscriptionRevenue({
        'filter[paid][_eq]': true,
        'filter[created_at][_gte]': iso(yearStart)
      }),
      this.sumSubscriptionRevenue({
        'filter[paid][_eq]': true,
        'filter[created_at][_gte]': iso(lastYearStart),
        'filter[created_at][_lt]': iso(lastYearSameDay)
      }),
      this.sumSubscriptionRevenue({
        'filter[paid][_eq]': true,
        'filter[created_at][_gte]': iso(thisMonthStart),
        'filter[created_at][_lt]': iso(nextMonthStart)
      }),
      this.sumEventOrderRevenue({
        'filter[status][_eq]': 'paid',
        'filter[date_created][_gte]': iso(yearStart)
      }),
      this.sumEventOrderRevenue({
        'filter[status][_eq]': 'paid',
        'filter[date_created][_gte]': iso(lastYearStart),
        'filter[date_created][_lt]': iso(lastYearSameDay)
      }),
      this.sumEventOrderRevenue({
        'filter[status][_eq]': 'paid',
        'filter[date_created][_gte]': iso(thisMonthStart),
        'filter[date_created][_lt]': iso(nextMonthStart)
      }),
      this.countCollection('events', {
        'filter[status][_eq]': 'published',
        'filter[start_date][_gte]': isoDate(today),
        'filter[start_date][_lt]': isoDate(in30Days)
      })
    ]);

    this.activeMembers = activeMembers;
    this.upcomingEvents = upcoming;
    this.birthdayMembers = birthdays;
    this.engagedMemberCount = engagement.engagedCount;
    this.engagementRate = activeMembers > 0
      ? (engagement.engagedCount / activeMembers) * 100
      : 0;
    this.guestConversionCount = guestConversion.converted;
    this.newMembersYtd = guestConversion.newMembersYtd;
    this.guestConversionRate = guestConversion.newMembersYtd > 0
      ? (guestConversion.converted / guestConversion.newMembersYtd) * 100
      : 0;
    this.membershipRevenueYtd = membershipYtd;
    this.membershipRevenueLastYearSamePeriod = membershipLastYearSame;
    this.ticketRevenueYtd = ticketYtd;
    this.ticketRevenueLastYearSamePeriod = ticketLastYearSame;
    this.combinedRevenueThisMonth = membershipThisMonth + ticketThisMonth;
    this.upcomingNext30Count = upcoming30;

    // Phase 2 — engagement panel (sequential because they depend on activeMembers being known)
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);

    await Promise.all([
      this.loadEngagementHistogram(iso(ninetyDaysAgo), isoDate(oneMonthAgo)),
      this.loadLeadershipBreakdown(iso(yearStart), iso(yearEnd)),
      this.loadTopReferrers(iso(thirtyDaysAgo)),
      this.loadEventPerformance(today, threeMonthsAgo),
      this.loadAcquisitionFunnel(sixMonthsAgo, yearStart),
      this.loadTopAcquisitionEvents(quarterStart),
      this.loadBoardHealth(today, ninetyDaysAgo, thirtyDaysAgo),
      this.loadActivePics(thisMonthStart, nextMonthStart),
      this.loadStaleDrafts(today),
      this.isRenewalSeason ? this.loadRenewalWatch(today) : Promise.resolve()
    ]);

    this.lastRefreshed = new Date();
    this.loading = false;
  }

  get isRenewalSeason(): boolean {
    const month = new Date().getMonth(); // 0-indexed: Oct = 9, Dec = 11
    return month >= 9 && month <= 11;
  }

  get renewalRateLabel(): string {
    if (this.renewalEligibleCount <= 0) return '—';
    const pct = (this.renewalRenewedCount / this.renewalEligibleCount) * 100;
    return `${pct.toFixed(0)}% renewed (${this.renewalRenewedCount}/${this.renewalEligibleCount})`;
  }

  get avgMemberReachLabel(): string {
    if (this.pastEventsLast3Months === 0) return '—';
    const avg = this.uniqueMembersLast3Months / this.pastEventsLast3Months;
    return avg.toFixed(1);
  }

  get dormantBucket(): EngagementBucket | null {
    return this.engagementBuckets.find((b) => b.key === 'dormant') ?? null;
  }

  openEngagementBucket(bucket: EngagementBucket): void {
    if (bucket.count === 0) return;
    this.activeBucket = bucket;
  }

  closeBucket(): void {
    this.activeBucket = null;
  }

  openBoardMember(member: BoardMember): void {
    this.activeBoardMember = member;
  }

  closeBoardMember(): void {
    this.activeBoardMember = null;
  }

  openRenewalAllModal(): void {
    this.showRenewalAllModal = true;
  }

  closeRenewalAllModal(): void {
    this.showRenewalAllModal = false;
  }

  boardLastSeenLabel(m: BoardMember): string {
    if (m.daysSinceLastEvent === null) return 'No events';
    if (m.daysSinceLastEvent === 0) return 'Today';
    if (m.daysSinceLastEvent === 1) return 'Yesterday';
    if (m.daysSinceLastEvent < 30) return `${m.daysSinceLastEvent}d ago`;
    return `${m.daysSinceLastEvent}d (flag)`;
  }

  private async sumSubscriptionRevenue(
    filter: Record<string, string | number | boolean | undefined>
  ): Promise<number> {
    try {
      const rows = await this.directus.list('subscription', {
        ...filter,
        fields: 'id,total',
        limit: 1000
      });
      return rows.reduce((sum, row) => sum + this.toNumber(row['total']), 0);
    } catch {
      return 0;
    }
  }

  private async sumEventOrderRevenue(
    filter: Record<string, string | number | boolean | undefined>
  ): Promise<number> {
    try {
      const rows = await this.directus.list('event_orders', {
        ...filter,
        fields: 'id,grand_total',
        limit: 1000
      });
      return rows.reduce((sum, row) => sum + this.toNumber(row['grand_total']), 0);
    } catch {
      return 0;
    }
  }

  private async loadEngagement(sinceIso: string): Promise<{ engagedCount: number }> {
    try {
      const rows = await this.directus.list('member_points', {
        'filter[type][_in]': 'event_attend,flagship_attend',
        'filter[date_created][_gte]': sinceIso,
        fields: 'profile_id',
        limit: 2000
      });
      const distinct = new Set<string>();
      for (const row of rows) {
        const id = row['profile_id'];
        if (typeof id === 'string' && id) distinct.add(id);
      }
      return { engagedCount: distinct.size };
    } catch {
      return { engagedCount: 0 };
    }
  }

  private async loadGuestConversion(
    yearStartDate: string
  ): Promise<{ converted: number; newMembersYtd: number }> {
    try {
      const newProfiles = await this.directus.list('profiles', {
        'filter[join_date][_gte]': yearStartDate,
        'filter[subscribed][_eq]': true,
        fields: 'id,email,join_date',
        limit: 1000
      });

      if (newProfiles.length === 0) {
        return { converted: 0, newMembersYtd: 0 };
      }

      // Build a lookup of new-member emails so we can scope the attendees query.
      const newMemberByEmail = new Map<string, number>();
      for (const p of newProfiles) {
        const email = typeof p['email'] === 'string' ? p['email'].trim().toLowerCase() : '';
        if (!email) continue;
        const joinedAt = typeof p['join_date'] === 'string'
          ? new Date(p['join_date']).getTime()
          : 0;
        newMemberByEmail.set(email, joinedAt);
      }

      const emails = Array.from(newMemberByEmail.keys());
      if (emails.length === 0) {
        return { converted: 0, newMembersYtd: newProfiles.length };
      }

      // Only count members who CHECKED IN to an event before they joined.
      const attendeeRows = await this.directus.list('event_attendees', {
        'filter[checkin_status][_eq]': 'checked_in',
        'filter[holder_email][_in]': emails.join(','),
        fields: 'holder_email,checked_in_at',
        limit: 2000
      });

      const convertedEmails = new Set<string>();
      for (const row of attendeeRows) {
        const email = typeof row['holder_email'] === 'string'
          ? row['holder_email'].trim().toLowerCase()
          : '';
        if (!email) continue;
        const joinedAt = newMemberByEmail.get(email);
        if (joinedAt === undefined) continue;
        const checkedInAt = typeof row['checked_in_at'] === 'string'
          ? new Date(row['checked_in_at']).getTime()
          : 0;
        if (checkedInAt > 0 && checkedInAt < joinedAt) {
          convertedEmails.add(email);
        }
      }

      return { converted: convertedEmails.size, newMembersYtd: newProfiles.length };
    } catch {
      return { converted: 0, newMembersYtd: 0 };
    }
  }

  private async loadRenewalWatch(today: Date): Promise<void> {
    try {
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;
      const yearEndIso = `${currentYear}-12-31`;

      // Eligible for renewal: members whose subscription_until is exactly Dec 31 of current year.
      // Members with bonus-plan carryover already have subscribed_until = next year Dec 31, so
      // filtering by _eq excludes them automatically.
      const eligibleProfiles = await this.directus.list('profiles', {
        'filter[subscribed_until][_eq]': yearEndIso,
        'filter[subscribed][_eq]': true,
        fields: 'id,first_name,last_name,email,member_rank,subscribed_until',
        sort: 'member_rank',
        limit: 1000
      });

      this.renewalEligibleCount = eligibleProfiles.length;

      if (eligibleProfiles.length === 0) {
        this.renewalRenewedCount = 0;
        this.renewalPending = [];
        return;
      }

      const profileIds = eligibleProfiles
        .map((p) => (typeof p['id'] === 'string' ? p['id'] : ''))
        .filter(Boolean);

      // Find which of those have a paid subscription row for next year
      const renewalRows = await this.directus.list('subscription', {
        'filter[paid][_eq]': true,
        'filter[year][_eq]': nextYear,
        'filter[profile_id][_in]': profileIds.join(','),
        fields: 'profile_id',
        limit: 1000
      });
      const renewedIds = new Set<string>();
      for (const r of renewalRows) {
        const id = typeof r['profile_id'] === 'string' ? r['profile_id'] : '';
        if (id) renewedIds.add(id);
      }
      this.renewalRenewedCount = renewedIds.size;

      // Build pending list
      this.renewalPending = eligibleProfiles
        .filter((p) => {
          const id = typeof p['id'] === 'string' ? p['id'] : '';
          return id && !renewedIds.has(id);
        })
        .map((p) => {
          const first = typeof p['first_name'] === 'string' ? p['first_name'].trim() : '';
          const last = typeof p['last_name'] === 'string' ? p['last_name'].trim() : '';
          const email = typeof p['email'] === 'string' ? p['email'].trim() : '';
          const name = [first, last].filter(Boolean).join(' ').trim() || email || 'Member';
          const rankRaw = p['member_rank'];
          const memberRank = typeof rankRaw === 'number'
            ? rankRaw
            : typeof rankRaw === 'string'
              ? Number.parseInt(rankRaw, 10) || null
              : null;
          const until = typeof p['subscribed_until'] === 'string' ? p['subscribed_until'] : '';
          const subscribedUntilLabel = until
            ? new Date(until).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
            : '—';
          return {
            id: typeof p['id'] === 'string' ? p['id'] : '',
            name,
            memberRank,
            subscribedUntilLabel
          };
        })
        .sort((a, b) => {
          // Founders/seniors first (lower rank = more senior)
          const ra = a.memberRank ?? Number.POSITIVE_INFINITY;
          const rb = b.memberRank ?? Number.POSITIVE_INFINITY;
          return ra - rb;
        });
    } catch {
      this.renewalEligibleCount = 0;
      this.renewalRenewedCount = 0;
      this.renewalPending = [];
    }
  }

  private async loadBoardHealth(
    today: Date,
    ninetyDaysAgo: Date,
    thirtyDaysAgo: Date
  ): Promise<void> {
    try {
      const currentYear = today.getFullYear();
      const lbodRows = await this.directus.list('lbodmembers', {
        'filter[year][_eq]': currentYear,
        fields: 'id,profile_id,name,position,rank',
        sort: 'rank',
        limit: 100
      });

      if (lbodRows.length === 0) {
        this.boardMembers = [];
        this.boardFlaggedCount = 0;
        return;
      }

      const profileIds = lbodRows
        .map((r) => (typeof r['profile_id'] === 'string' ? r['profile_id'] : ''))
        .filter(Boolean);

      // Resolve profiles for any missing names
      const nameByProfile = new Map<string, string>();
      if (profileIds.length > 0) {
        try {
          const profileRows = await this.directus.list('profiles', {
            'filter[id][_in]': profileIds.join(','),
            fields: 'id,first_name,last_name,email',
            limit: 200
          });
          for (const p of profileRows) {
            const id = typeof p['id'] === 'string' ? p['id'] : '';
            const first = typeof p['first_name'] === 'string' ? p['first_name'].trim() : '';
            const last = typeof p['last_name'] === 'string' ? p['last_name'].trim() : '';
            const email = typeof p['email'] === 'string' ? p['email'].trim() : '';
            nameByProfile.set(id, [first, last].filter(Boolean).join(' ').trim() || email || 'Member');
          }
        } catch {
          // ignore
        }
      }

      // Pull all event_attend / flagship_attend points for these profiles in last 90d
      // Bring source_event_id so we can list recent event titles in the modal.
      const pointRows = profileIds.length > 0
        ? await this.directus.list('member_points', {
            'filter[type][_in]': 'event_attend,flagship_attend',
            'filter[date_created][_gte]': ninetyDaysAgo.toISOString(),
            'filter[profile_id][_in]': profileIds.join(','),
            fields: 'profile_id,date_created,source_event_id',
            limit: 5000
          })
        : [];

      const pointsByProfile = new Map<string, Array<{ ts: number; eventId: string }>>();
      const eventIdSet = new Set<string>();
      for (const row of pointRows) {
        const pid = typeof row['profile_id'] === 'string' ? row['profile_id'] : '';
        if (!pid) continue;
        const ts = typeof row['date_created'] === 'string'
          ? new Date(row['date_created']).getTime()
          : 0;
        const eid = row['source_event_id'] != null ? String(row['source_event_id']) : '';
        if (eid) eventIdSet.add(eid);
        const list = pointsByProfile.get(pid) ?? [];
        list.push({ ts, eventId: eid });
        pointsByProfile.set(pid, list);
      }

      // Resolve event titles for recent attendance
      const eventTitleById = new Map<string, string>();
      if (eventIdSet.size > 0) {
        try {
          const eventRows = await this.directus.list('events', {
            'filter[id][_in]': Array.from(eventIdSet).join(','),
            fields: 'id,title',
            limit: 200
          });
          for (const e of eventRows) {
            const id = String(e['id'] ?? '');
            const title = typeof e['title'] === 'string' ? e['title'] : `Event ${id}`;
            eventTitleById.set(id, title);
          }
        } catch {
          // ignore
        }
      }

      const thirtyMs = thirtyDaysAgo.getTime();
      const todayMs = today.getTime();

      const members: BoardMember[] = lbodRows.map((row) => {
        const id = String(row['id'] ?? '');
        const profileId = typeof row['profile_id'] === 'string' ? row['profile_id'] : '';
        const fallbackName = typeof row['name'] === 'string' ? row['name'] : 'Member';
        const name = nameByProfile.get(profileId) ?? fallbackName;
        const position = typeof row['position'] === 'string' ? row['position'] : '';
        const points = pointsByProfile.get(profileId) ?? [];

        const sortedDesc = points.slice().sort((a, b) => b.ts - a.ts);
        const lastTs = sortedDesc[0]?.ts ?? 0;
        const daysSinceLastEvent = lastTs > 0
          ? Math.max(0, Math.floor((todayMs - lastTs) / (24 * 60 * 60 * 1000)))
          : null;
        const attendedCount30 = points.filter((p) => p.ts >= thirtyMs).length;
        const flagged = attendedCount30 === 0;

        const recentEventTitles: string[] = [];
        const seen = new Set<string>();
        for (const p of sortedDesc) {
          if (recentEventTitles.length >= 5) break;
          if (!p.eventId || seen.has(p.eventId)) continue;
          seen.add(p.eventId);
          recentEventTitles.push(eventTitleById.get(p.eventId) ?? `Event ${p.eventId}`);
        }

        return {
          id,
          profileId,
          name,
          position,
          daysSinceLastEvent,
          attendedCount30,
          flagged,
          recentEventTitles
        };
      });

      // Sort: flagged first (most days since last event first), then non-flagged by least recent.
      members.sort((a, b) => {
        if (a.flagged !== b.flagged) return a.flagged ? -1 : 1;
        const aDays = a.daysSinceLastEvent ?? Number.POSITIVE_INFINITY;
        const bDays = b.daysSinceLastEvent ?? Number.POSITIVE_INFINITY;
        return bDays - aDays;
      });

      this.boardMembers = members;
      this.boardFlaggedCount = members.filter((m) => m.flagged).length;
    } catch {
      this.boardMembers = [];
      this.boardFlaggedCount = 0;
    }
  }

  private async loadActivePics(thisMonthStart: Date, nextMonthStart: Date): Promise<void> {
    try {
      const rows = await this.directus.list('events', {
        'filter[status][_in]': 'published,closed',
        'filter[start_date][_gte]': thisMonthStart.toISOString(),
        'filter[start_date][_lt]': nextMonthStart.toISOString(),
        'filter[pic_id][_nnull]': true,
        fields: 'pic_id',
        limit: 500
      });
      const distinct = new Set<string>();
      for (const r of rows) {
        const pid = typeof r['pic_id'] === 'string' ? r['pic_id'] : '';
        if (pid) distinct.add(pid);
      }
      this.activePicsThisMonth = distinct.size;
    } catch {
      this.activePicsThisMonth = 0;
    }
  }

  private async loadStaleDrafts(today: Date): Promise<void> {
    try {
      const fourteenDaysAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
      const rows = await this.directus.list('events', {
        'filter[status][_eq]': 'draft',
        'filter[created_at][_lt]': fourteenDaysAgo.toISOString(),
        fields: 'id,title,created_at',
        sort: 'created_at',
        limit: 50
      });
      const todayMs = today.getTime();
      this.staleDraftEvents = rows
        .map((r) => {
          const id = String(r['id'] ?? '');
          const title = typeof r['title'] === 'string' ? r['title'] : 'Untitled draft';
          const createdTs = typeof r['created_at'] === 'string'
            ? new Date(r['created_at']).getTime()
            : 0;
          const ageDays = createdTs > 0
            ? Math.floor((todayMs - createdTs) / (24 * 60 * 60 * 1000))
            : 0;
          return { id, title, ageDays };
        })
        .sort((a, b) => b.ageDays - a.ageDays)
        .slice(0, 5);
    } catch {
      this.staleDraftEvents = [];
    }
  }

  private async loadEventPerformance(today: Date, threeMonthsAgo: Date): Promise<void> {
    try {
      // Past events: status published, start_date <= today, ordered desc
      const pastRows = await this.directus.list('events', {
        'filter[status][_eq]': 'published',
        'filter[start_date][_lte]': today.toISOString(),
        sort: '-start_date',
        fields: 'id,title,start_date,eventcategory_id',
        limit: 100
      });

      if (pastRows.length === 0) {
        this.recentEvents = [];
        this.categoryMix = [];
        this.pastEventsLast3Months = 0;
        this.uniqueMembersLast3Months = 0;
        return;
      }

      const last3 = pastRows.slice(0, 3);
      const eventIds = last3
        .map((r) => String(r['id'] ?? ''))
        .filter(Boolean);

      // Pull attendees for the last 3 events to compute registered + checked_in
      const attendeeRows = eventIds.length > 0
        ? await this.directus.list('event_attendees', {
            'filter[event_id][_in]': eventIds.join(','),
            fields: 'event_id,checkin_status',
            limit: 5000
          })
        : [];

      const stats = new Map<string, { registered: number; checkedIn: number }>();
      for (const id of eventIds) stats.set(id, { registered: 0, checkedIn: 0 });
      for (const row of attendeeRows) {
        const id = String(row['event_id'] ?? '');
        const entry = stats.get(id);
        if (!entry) continue;
        entry.registered++;
        if (row['checkin_status'] === 'checked_in') entry.checkedIn++;
      }

      this.recentEvents = last3.map((r) => {
        const id = String(r['id'] ?? '');
        const s = stats.get(id) ?? { registered: 0, checkedIn: 0 };
        const noShowPct = s.registered > 0
          ? Math.round(((s.registered - s.checkedIn) / s.registered) * 100)
          : 0;
        const start = typeof r['start_date'] === 'string' ? new Date(r['start_date']) : null;
        return {
          id,
          title: typeof r['title'] === 'string' ? r['title'] : 'Untitled event',
          dateLabel: start
            ? start.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
            : '—',
          registered: s.registered,
          checkedIn: s.checkedIn,
          noShowPct
        };
      });

      // Reach: events in last 3 months
      const recentEventsIn3Months = pastRows.filter((r) => {
        const start = typeof r['start_date'] === 'string' ? new Date(r['start_date']) : null;
        return start && start >= threeMonthsAgo;
      });
      const recentIds = recentEventsIn3Months
        .map((r) => String(r['id'] ?? ''))
        .filter(Boolean);
      this.pastEventsLast3Months = recentIds.length;

      if (recentIds.length > 0) {
        const reachRows = await this.directus.list('event_attendees', {
          'filter[event_id][_in]': recentIds.join(','),
          'filter[checkin_status][_eq]': 'checked_in',
          'filter[profile_id][_nnull]': true,
          fields: 'profile_id',
          limit: 5000
        });
        const distinct = new Set<string>();
        for (const row of reachRows) {
          const pid = typeof row['profile_id'] === 'string' ? row['profile_id'] : '';
          if (pid) distinct.add(pid);
        }
        this.uniqueMembersLast3Months = distinct.size;
      } else {
        this.uniqueMembersLast3Months = 0;
      }

      // Category mix YTD: count events held this year by eventcategory_id
      const yearStartTs = new Date(new Date().getFullYear(), 0, 1).getTime();
      const ytdEvents = pastRows.filter((r) => {
        const start = typeof r['start_date'] === 'string' ? new Date(r['start_date']).getTime() : 0;
        return start >= yearStartTs;
      });
      const catCounts = new Map<string, number>();
      for (const r of ytdEvents) {
        const cat = r['eventcategory_id'];
        const key = cat == null ? 'uncategorised' : String(cat);
        catCounts.set(key, (catCounts.get(key) ?? 0) + 1);
      }

      // Resolve category names
      const categoryIds = Array.from(catCounts.keys()).filter((k) => k !== 'uncategorised');
      const nameById = new Map<string, string>();
      if (categoryIds.length > 0) {
        try {
          const catRows = await this.directus.list('eventcategories', {
            'filter[id][_in]': categoryIds.join(','),
            fields: 'id,name',
            limit: 200
          });
          for (const c of catRows) {
            const id = String(c['id'] ?? '');
            const name = typeof c['name'] === 'string' ? c['name'] : `Category ${id}`;
            nameById.set(id, name);
          }
        } catch {
          // fall through; use raw ids
        }
      }

      const totalYtd = ytdEvents.length || 1;
      this.categoryMix = Array.from(catCounts.entries())
        .map(([key, count]) => ({
          id: key,
          label: key === 'uncategorised' ? 'Uncategorised' : nameById.get(key) ?? `Category ${key}`,
          count,
          percent: Math.round((count / totalYtd) * 100)
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    } catch {
      this.recentEvents = [];
      this.categoryMix = [];
      this.pastEventsLast3Months = 0;
      this.uniqueMembersLast3Months = 0;
    }
  }

  private async loadAcquisitionFunnel(sixMonthsAgo: Date, yearStart: Date): Promise<void> {
    try {
      // Pull all guest check-ins since 6 months ago (covers both windows).
      const rows = await this.directus.list('event_attendees', {
        'filter[checkin_status][_eq]': 'checked_in',
        'filter[guest_contact_id][_nnull]': true,
        'filter[checked_in_at][_gte]': sixMonthsAgo.toISOString(),
        fields: 'guest_contact_id,event_id,checked_in_at,holder_email',
        limit: 5000
      });

      const sixMs = sixMonthsAgo.getTime();
      const ytdMs = yearStart.getTime();

      type GuestStats = {
        firstCheckin: number;
        eventIds: Set<string>;
        emailLower: string;
      };
      const guests = new Map<string, GuestStats>();

      for (const row of rows) {
        const gid = typeof row['guest_contact_id'] === 'string' ? row['guest_contact_id'] : '';
        if (!gid) continue;
        const ts = typeof row['checked_in_at'] === 'string'
          ? new Date(row['checked_in_at']).getTime()
          : 0;
        const eventId = String(row['event_id'] ?? '');
        const email = typeof row['holder_email'] === 'string'
          ? row['holder_email'].trim().toLowerCase()
          : '';

        let g = guests.get(gid);
        if (!g) {
          g = { firstCheckin: ts, eventIds: new Set(), emailLower: email };
          guests.set(gid, g);
        }
        if (ts > 0 && (g.firstCheckin === 0 || ts < g.firstCheckin)) g.firstCheckin = ts;
        if (eventId) g.eventIds.add(eventId);
        if (email && !g.emailLower) g.emailLower = email;
      }

      // Stage 1: distinct guests with ≥1 check-in in window
      const stage1_6 = Array.from(guests.values()).filter((g) => g.firstCheckin >= sixMs);
      const stage1_ytd = Array.from(guests.values()).filter((g) => g.firstCheckin >= ytdMs);

      // Stage 2: of stage 1, guests with check-ins to ≥2 distinct events
      const stage2_6 = stage1_6.filter((g) => g.eventIds.size >= 2);
      const stage2_ytd = stage1_ytd.filter((g) => g.eventIds.size >= 2);

      // Stage 3: of stage 1 (not stage 2), guests whose email later became a profile
      const allEmails = Array.from(
        new Set(
          [...stage1_6, ...stage1_ytd]
            .map((g) => g.emailLower)
            .filter((e) => !!e)
        )
      );

      const profileJoinByEmail = new Map<string, number>();
      if (allEmails.length > 0) {
        try {
          const profileRows = await this.directus.list('profiles', {
            'filter[email][_in]': allEmails.join(','),
            'filter[subscribed][_eq]': true,
            fields: 'email,join_date',
            limit: 1000
          });
          for (const p of profileRows) {
            const email = typeof p['email'] === 'string' ? p['email'].trim().toLowerCase() : '';
            if (!email) continue;
            const joinTs = typeof p['join_date'] === 'string'
              ? new Date(p['join_date']).getTime()
              : 0;
            profileJoinByEmail.set(email, joinTs);
          }
        } catch {
          // ignore — we'll fall through with empty map
        }
      }

      const isConverted = (g: GuestStats) => {
        if (!g.emailLower) return false;
        const joinTs = profileJoinByEmail.get(g.emailLower);
        if (joinTs === undefined || joinTs === 0) return false;
        return joinTs > g.firstCheckin;
      };

      const stage3_6 = stage1_6.filter(isConverted);
      const stage3_ytd = stage1_ytd.filter(isConverted);

      const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0);

      this.acquisitionFunnel = [
        {
          label: 'Guest check-ins',
          count6: stage1_6.length,
          countYtd: stage1_ytd.length,
          convPct6: null,
          convPctYtd: null
        },
        {
          label: 'Repeat guests',
          count6: stage2_6.length,
          countYtd: stage2_ytd.length,
          convPct6: pct(stage2_6.length, stage1_6.length),
          convPctYtd: pct(stage2_ytd.length, stage1_ytd.length)
        },
        {
          label: 'Converted',
          count6: stage3_6.length,
          countYtd: stage3_ytd.length,
          convPct6: pct(stage3_6.length, stage1_6.length),
          convPctYtd: pct(stage3_ytd.length, stage1_ytd.length)
        }
      ];
    } catch {
      this.acquisitionFunnel = [
        { label: 'Guest check-ins', count6: 0, countYtd: 0, convPct6: null, convPctYtd: null },
        { label: 'Repeat guests', count6: 0, countYtd: 0, convPct6: 0, convPctYtd: 0 },
        { label: 'Converted', count6: 0, countYtd: 0, convPct6: 0, convPctYtd: 0 }
      ];
    }
  }

  private async loadTopAcquisitionEvents(quarterStart: Date): Promise<void> {
    try {
      // Guest check-ins this quarter
      const rows = await this.directus.list('event_attendees', {
        'filter[checkin_status][_eq]': 'checked_in',
        'filter[guest_contact_id][_nnull]': true,
        'filter[checked_in_at][_gte]': quarterStart.toISOString(),
        fields: 'event_id,holder_email,checked_in_at',
        limit: 5000
      });

      // Group by event, with the set of guest emails per event
      const perEvent = new Map<string, { emails: Set<string>; firstByEmail: Map<string, number> }>();
      for (const row of rows) {
        const eid = String(row['event_id'] ?? '');
        const email = typeof row['holder_email'] === 'string'
          ? row['holder_email'].trim().toLowerCase()
          : '';
        const ts = typeof row['checked_in_at'] === 'string'
          ? new Date(row['checked_in_at']).getTime()
          : 0;
        if (!eid || !email) continue;
        let entry = perEvent.get(eid);
        if (!entry) {
          entry = { emails: new Set(), firstByEmail: new Map() };
          perEvent.set(eid, entry);
        }
        entry.emails.add(email);
        const existing = entry.firstByEmail.get(email);
        if (existing === undefined || ts < existing) entry.firstByEmail.set(email, ts);
      }

      const allEmails = Array.from(
        new Set(Array.from(perEvent.values()).flatMap((e) => Array.from(e.emails)))
      );

      const profileJoinByEmail = new Map<string, number>();
      if (allEmails.length > 0) {
        try {
          const profileRows = await this.directus.list('profiles', {
            'filter[email][_in]': allEmails.join(','),
            'filter[subscribed][_eq]': true,
            fields: 'email,join_date',
            limit: 1000
          });
          for (const p of profileRows) {
            const email = typeof p['email'] === 'string' ? p['email'].trim().toLowerCase() : '';
            const joinTs = typeof p['join_date'] === 'string'
              ? new Date(p['join_date']).getTime()
              : 0;
            if (email) profileJoinByEmail.set(email, joinTs);
          }
        } catch {
          // ignore
        }
      }

      const ranked: Array<{ id: string; converted: number }> = [];
      for (const [eid, entry] of perEvent.entries()) {
        let converted = 0;
        for (const email of entry.emails) {
          const joinTs = profileJoinByEmail.get(email);
          const firstCheckin = entry.firstByEmail.get(email) ?? 0;
          if (joinTs && firstCheckin && joinTs > firstCheckin) converted++;
        }
        if (converted > 0) ranked.push({ id: eid, converted });
      }

      ranked.sort((a, b) => b.converted - a.converted);
      const top = ranked.slice(0, 3);

      if (top.length === 0) {
        this.topAcquisitionEvents = [];
        return;
      }

      const eventRows = await this.directus.list('events', {
        'filter[id][_in]': top.map((t) => t.id).join(','),
        fields: 'id,title',
        limit: 50
      });
      const titleById = new Map<string, string>();
      for (const e of eventRows) {
        const id = String(e['id'] ?? '');
        const title = typeof e['title'] === 'string' ? e['title'] : `Event ${id}`;
        titleById.set(id, title);
      }

      this.topAcquisitionEvents = top.map((t) => ({
        id: t.id,
        title: titleById.get(t.id) ?? `Event ${t.id}`,
        converted: t.converted
      }));
    } catch {
      this.topAcquisitionEvents = [];
    }
  }

  private async loadEngagementHistogram(
    sinceIso: string,
    eligibleJoinedBeforeDate: string
  ): Promise<void> {
    try {
      // Eligible members: subscribed_until in the future AND joined ≥ 1 month ago.
      const todayIso = new Date().toISOString().slice(0, 10);
      const eligibleMembers = await this.directus.list('profiles', {
        'filter[subscribed_until][_gte]': todayIso,
        'filter[join_date][_lte]': eligibleJoinedBeforeDate,
        fields: 'id,first_name,last_name,email',
        limit: 1000
      });

      const members = new Map<string, { name: string; count: number; lastTs: number }>();
      for (const p of eligibleMembers) {
        const id = String(p['id'] ?? '');
        if (!id) continue;
        const first = typeof p['first_name'] === 'string' ? p['first_name'].trim() : '';
        const last = typeof p['last_name'] === 'string' ? p['last_name'].trim() : '';
        const email = typeof p['email'] === 'string' ? p['email'].trim() : '';
        const name = [first, last].filter(Boolean).join(' ').trim() || email || 'Member';
        members.set(id, { name, count: 0, lastTs: 0 });
      }

      this.engagementEligibleCount = members.size;

      if (members.size === 0) {
        this.resetEngagementBuckets();
        this.dormantPreview = [];
        return;
      }

      // Pull all event_attend / flagship_attend point rows in the window for these members.
      const ids = Array.from(members.keys());
      const pointRows = await this.directus.list('member_points', {
        'filter[type][_in]': 'event_attend,flagship_attend',
        'filter[date_created][_gte]': sinceIso,
        'filter[profile_id][_in]': ids.join(','),
        fields: 'profile_id,date_created',
        limit: 5000
      });

      for (const row of pointRows) {
        const id = typeof row['profile_id'] === 'string' ? row['profile_id'] : '';
        if (!id) continue;
        const entry = members.get(id);
        if (!entry) continue;
        entry.count++;
        const ts = typeof row['date_created'] === 'string'
          ? new Date(row['date_created']).getTime()
          : 0;
        if (ts > entry.lastTs) entry.lastTs = ts;
      }

      const buckets: Record<EngagementBucket['key'], EngagementMember[]> = {
        dormant: [], casual: [], active: [], power: []
      };

      for (const [id, info] of members.entries()) {
        const member: EngagementMember = {
          id,
          name: info.name,
          count: info.count,
          lastEventLabel: info.lastTs > 0
            ? `Last event ${this.relativeDays(info.lastTs)}`
            : 'No events in 90 days'
        };
        if (info.count === 0) buckets.dormant.push(member);
        else if (info.count <= 2) buckets.casual.push(member);
        else if (info.count <= 5) buckets.active.push(member);
        else buckets.power.push(member);
      }

      // Sort each bucket: dormant by name; others by count descending then name.
      buckets.dormant.sort((a, b) => a.name.localeCompare(b.name));
      for (const k of ['casual', 'active', 'power'] as const) {
        buckets[k].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
      }

      this.engagementBuckets = this.engagementBuckets.map((b) => ({
        ...b,
        members: buckets[b.key],
        count: buckets[b.key].length
      }));

      this.dormantPreview = buckets.dormant.slice(0, 5);
    } catch {
      this.resetEngagementBuckets();
      this.dormantPreview = [];
    }
  }

  private resetEngagementBuckets(): void {
    this.engagementBuckets = this.engagementBuckets.map((b) => ({ ...b, members: [], count: 0 }));
  }

  private async loadLeadershipBreakdown(
    yearStartIso: string,
    yearEndIso: string
  ): Promise<void> {
    // Tracks "willing to serve" signals — excludes elected/expected roles
    // (local_president, lbod, national, international, other).
    const roleTypes: Array<{ key: string; label: string }> = [
      { key: 'project_director', label: 'Project directors' },
      { key: 'committee', label: 'Committee members' },
      { key: 'speaker', label: 'Speakers' },
      { key: 'mentor', label: 'Mentors' },
      { key: 'volunteer', label: 'Volunteers' }
    ];

    try {
      const rows = await this.directus.list('experiences', {
        'filter[status][_eq]': 'approved',
        'filter[role_type][_in]': roleTypes.map((r) => r.key).join(','),
        'filter[startdate][_gte]': yearStartIso,
        'filter[startdate][_lte]': yearEndIso,
        fields: 'profile_id,role_type',
        limit: 5000
      });

      const perRole = new Map<string, Set<string>>();
      const allMembers = new Set<string>();
      for (const r of roleTypes) perRole.set(r.key, new Set());

      for (const row of rows) {
        const type = typeof row['role_type'] === 'string' ? row['role_type'] : '';
        const id = typeof row['profile_id'] === 'string' ? row['profile_id'] : '';
        if (!type || !id) continue;
        perRole.get(type)?.add(id);
        allMembers.add(id);
      }

      this.leadershipBreakdown = roleTypes.map((r) => ({
        key: r.key,
        label: r.label,
        count: perRole.get(r.key)?.size ?? 0
      }));
      this.leadershipDistinctMembers = allMembers.size;
    } catch {
      this.leadershipBreakdown = roleTypes.map((r) => ({ key: r.key, label: r.label, count: 0 }));
      this.leadershipDistinctMembers = 0;
    }
  }

  private async loadTopReferrers(sinceIso: string): Promise<void> {
    try {
      const rows = await this.directus.list('member_points', {
        'filter[type][_eq]': 'referral',
        'filter[date_created][_gte]': sinceIso,
        fields: 'profile_id',
        limit: 1000
      });

      const counts = new Map<string, number>();
      for (const row of rows) {
        const id = typeof row['profile_id'] === 'string' ? row['profile_id'] : '';
        if (!id) continue;
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }

      const top = Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      if (top.length === 0) {
        this.topReferrers = [];
        return;
      }

      const profiles = await this.directus.list('profiles', {
        'filter[id][_in]': top.map(([id]) => id).join(','),
        fields: 'id,first_name,last_name,email',
        limit: 50
      });

      const nameById = new Map<string, string>();
      for (const p of profiles) {
        const id = typeof p['id'] === 'string' ? p['id'] : '';
        const first = typeof p['first_name'] === 'string' ? p['first_name'].trim() : '';
        const last = typeof p['last_name'] === 'string' ? p['last_name'].trim() : '';
        const email = typeof p['email'] === 'string' ? p['email'].trim() : '';
        nameById.set(id, [first, last].filter(Boolean).join(' ').trim() || email || 'Member');
      }

      this.topReferrers = top.map(([id, count]) => ({
        id,
        name: nameById.get(id) ?? 'Member',
        count
      }));
    } catch {
      this.topReferrers = [];
    }
  }

  private relativeDays(ts: number): string {
    if (!ts) return '—';
    const diffMs = Date.now() - ts;
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 60) return '1 month ago';
    return `${Math.floor(days / 30)} months ago`;
  }

  private toNumber(raw: unknown): number {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
    if (typeof raw === 'string') {
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  }

  private async countCollection(
    collection: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<number> {
    try {
      const rows = await this.directus.list(collection, { ...query, limit: 500, fields: 'id' });
      return rows.length;
    } catch {
      return 0;
    }
  }

  private async loadUpcomingEvents(today: Date): Promise<UpcomingEvent[]> {
    try {
      const rows = await this.directus.list('events', {
        fields: 'id,title,start_date,status',
        sort: 'start_date',
        limit: 20
      });

      return rows
        .map((row) => {
          const startRaw = row['start_date'];
          const date = typeof startRaw === 'string' ? new Date(startRaw) : null;
          const status = typeof row['status'] === 'string' ? row['status'] : '';
          return {
            id: String(row['id'] ?? ''),
            title: typeof row['title'] === 'string' ? row['title'] : 'Untitled event',
            date,
            status
          };
        })
        .filter((event) => event.date && !Number.isNaN(event.date.getTime()))
        .filter((event) => event.date && event.date >= today)
        .filter((event) => event.status !== 'cancelled')
        .slice(0, 6)
        .map((event) => ({
          id: event.id,
          title: event.title,
          dateLabel: event.date
            ? event.date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
            : 'Date not set'
        }));
    } catch {
      return [];
    }
  }

  private async loadBirthdaysToday(today: Date): Promise<BirthdayMember[]> {
    try {
      const rows = await this.directus.list('profiles', {
        fields: 'id,first_name,last_name,dob,email',
        'filter[subscribed][_eq]': true,
        limit: 500
      });

      return rows
        .filter((row) => typeof row['dob'] === 'string' && row['dob'])
        .map((row) => {
          const date = new Date(String(row['dob']));
          const firstName = typeof row['first_name'] === 'string' ? row['first_name'].trim() : '';
          const lastName = typeof row['last_name'] === 'string' ? row['last_name'].trim() : '';
          const splitName = [firstName, lastName].filter(Boolean).join(' ').trim();
          const email = typeof row['email'] === 'string' ? row['email'].trim() : '';
          const name = splitName || email || 'Member';
          return { id: String(row['id']), date, name };
        })
        .filter((entry) => !Number.isNaN(entry.date.getTime()))
        .filter((entry) => entry.date.getMonth() === today.getMonth() && entry.date.getDate() === today.getDate())
        .slice(0, 8)
        .map((entry) => ({ id: entry.id, name: entry.name }));
    } catch {
      return [];
    }
  }
}
